/* eslint-disable max-lines */
import type {SharedData, UnitSystem} from '@gaia/types';
import getAreaLines from '@gaia/utils/gaia/getAreaLines';
import getAreaPolygon from '@gaia/utils/gaia/getAreaPolygon';
import isPolygonValid from '@gaia/utils/gaia/isPolygonValid';
import concatMapboxStyles from '@gaia/utils/mapbox/concatMapboxStyles';
import type {Feature, Polygon} from 'geojson';
import compact from 'lodash/compact';
import fromPairs from 'lodash/fromPairs';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';
import values from 'lodash/values';

import {areaFixedLayerIds} from './fixedLayerIds';

type LocalFixedLayerIds = (typeof areaFixedLayerIds)['localUser'];
type RemoteFixedLayerIds = (typeof areaFixedLayerIds)['remoteUser'];
type EditingFixedLayerIds = (typeof areaFixedLayerIds)['editingArea'];

interface GetAreasMapboxStyleOptions {
    sharedData: SharedData;
    interaction: InteractionState;
    isPrinting: boolean;
    showAreas: boolean;
    areas: {[id: string]: Area};
    unitSystem: UnitSystem;
    isAuthenticated: boolean;
    selections: SelectionState[];
    userSource: mapboxgl.VectorSource | undefined;
    visibilities: Record<string, boolean>;
    showArchived: boolean;
    activeFolderIds: string[] | undefined;
}

export default function getStyle(options: GetAreasMapboxStyleOptions) {
    return {
        metadata: {
            id: 'areas',
        },
        ...concatMapboxStyles(
            compact([
                getRemoteUserStyle(areaFixedLayerIds.remoteUser, options),
                getLocalUserStyle(areaFixedLayerIds.localUser, options),
                getRemoteSharedStyle(areaFixedLayerIds.remoteShared, options),
                getLocalSharedStyle(areaFixedLayerIds.localShared, options),
                getEditingAreaStyle(areaFixedLayerIds.editingArea, options),
            ])
        ),
        glyphs: 'https://static.gaiagps.com/GaiaTopoGL/glyphs/{fontstack}/{range}.pbf',
    };
}

function getEditingAreaStyle(
    layerIds: EditingFixedLayerIds,
    {
        interaction,
        isPrinting,
        selections,
        unitSystem,
        visibilities,
        activeFolderIds,
    }: GetAreasMapboxStyleOptions
): mapboxgl.Style | undefined {
    if (
        interaction.type !== 'editingArea' ||
        !interaction.area.isGeometryLoaded ||
        !interaction.area.isGeometryEditable
    ) {
        return undefined;
    }

    const {area} = interaction;
    const polygon = getAreaPolygon(area, interaction)!;

    const isValid = isPolygonValid(polygon);

    const areaPoints = values(area.points);

    const areaLines = getAreaLines(area, unitSystem, interaction);

    const feature: Feature<Polygon, Area['properties']> = {
        type: 'Feature',
        properties: area.properties,
        geometry: polygon,
    };

    return {
        version: 8,
        metadata: {
            id: 'editingArea',
        },
        sources: {
            source: {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [feature, ...areaPoints, ...areaLines],
                },
            },
        },
        layers: compact([
            getFillLayer({
                id: layerIds.fill,
                interaction,
                selections,
                paint: {
                    'fill-color': area.properties.color,
                },
                isPrinting,
                visibilities,
                filter: ['all'],
                activeFolderIds,
            }),
            getLineLayer({
                id: layerIds.line,
                interaction,
                selections,
                paint: {'line-color': area.properties.color},
                isPrinting,
                visibilities,
                filter: ['all'],
                activeFolderIds,
            }),
            {
                id: layerIds.point,
                filter: ['all', ['==', ['geometry-type'], 'Point']],
                type: 'circle',
                source: 'source',
                paint: {
                    'circle-color': area.properties.color,
                    'circle-radius': 8,
                    'circle-stroke-color': '#fff',
                    'circle-stroke-width': 1,
                },
            },
            // Distances only display if the polygon is valid
            isValid && {
                id: layerIds.symbol,
                type: 'symbol',
                source: 'source',
                filter: ['all', ['==', ['geometry-type'], 'LineString']],
                paint: {
                    'text-color': area.properties.color,
                    'text-halo-color': '#fff',
                    'text-halo-width': 1,
                },
                layout: {
                    'text-field': ['get', 'length'],
                    'symbol-placement': 'line-center',
                    'text-font': ['Bold'],
                    'text-size': 12,
                    'text-line-height': 1,
                    'text-anchor': 'center',
                    'text-justify': 'center',
                },
            },
        ]),
    };
}

function getLocalUserStyle(
    layerIds: LocalFixedLayerIds,
    {
        interaction,
        showAreas,
        areas,
        sharedData,
        selections,
        isPrinting,
        visibilities,
        showArchived,
        activeFolderIds,
    }: GetAreasMapboxStyleOptions
): mapboxgl.Style | undefined {
    const editingArea = interaction.type === 'editingArea' && interaction.area;

    const features = compact(
        values(areas)
            // Show areas that are not deleted/archived, not shared data, and not being edited
            .filter(
                (area: Area) =>
                    (showArchived || (!area.properties.deleted && !area.properties.archived)) &&
                    area.isGeometryLoaded &&
                    (!editingArea || area.properties.id !== editingArea.properties.id) &&
                    !isSharedData(sharedData, area)
            )
            .map((area: Area): Feature<Polygon, Area['properties']> | undefined => {
                const geometry = getAreaPolygon(area, interaction);
                if (!geometry) return undefined;

                return {
                    type: 'Feature',
                    properties: area.properties,
                    geometry,
                };
            })
    );

    if (!features.length) return;

    const colorExpression: mapboxgl.Expression = [
        'get',
        ['coalesce', ['get', 'localId'], ['get', 'id']],
        ['literal', mapValues(areas, (area) => area.properties.color)],
    ];

    const filter = getLocalFilter(
        areas,
        visibilities,
        interaction,
        selections,
        sharedData,
        showArchived,
        showAreas,
        activeFolderIds
    );

    return {
        version: 8,
        metadata: {
            id: 'localUser',
        },
        sources: {
            source: {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features,
                },
            },
        },
        layers: compact([
            getFillLayer({
                id: layerIds.fill,
                interaction,
                selections,
                paint: {
                    'fill-color': colorExpression,
                },
                isPrinting,
                visibilities,
                filter,
                activeFolderIds,
            }),
            getLineLayer({
                id: layerIds.line,
                interaction,
                selections,
                paint: {'line-color': colorExpression},
                isPrinting,
                visibilities,
                filter,
                activeFolderIds,
            }),
        ]),
    };
}

function getRemoteUserStyle(
    layerIds: RemoteFixedLayerIds,
    {
        interaction,
        showAreas,
        areas,
        sharedData,
        isAuthenticated,
        selections,
        isPrinting,
        visibilities,
        showArchived,
        userSource,
        activeFolderIds,
    }: GetAreasMapboxStyleOptions
): mapboxgl.Style | undefined {
    if (!isAuthenticated || !userSource) return;

    const filter = [
        'all',
        getRemoteFilter(
            areas,
            visibilities,
            interaction,
            selections,
            sharedData,
            showArchived,
            showAreas,
            activeFolderIds
        ),
        sharedData && sharedData.type === 'area'
            ? ['!=', ['get', 'id'], sharedData.id]
            : ['literal', true],
    ];

    return {
        version: 8,
        metadata: {
            id: 'remoteUser',
        },
        sources: {
            source: userSource,
        },
        layers: [
            getFillLayer({
                id: layerIds.fill,
                interaction,
                selections,
                filter,
                'source-layer': 'areas',
                isPrinting,
                visibilities,
                activeFolderIds,
            }),
            getLineLayer({
                id: layerIds.line,
                interaction,
                selections,
                filter,
                'source-layer': 'areas',
                isPrinting,
                visibilities,
                activeFolderIds,
            }),
        ],
    };
}

function getRemoteSharedStyle(
    layerIds: RemoteFixedLayerIds,
    {
        areas,
        interaction,
        sharedData,
        selections,
        isPrinting,
        showAreas,
        visibilities,
        showArchived,
        activeFolderIds,
    }: GetAreasMapboxStyleOptions
): mapboxgl.Style | undefined {
    const source = getRemoteSharedSource(sharedData);
    if (!source) return;

    const filter = getRemoteFilter(
        areas,
        visibilities,
        interaction,
        selections,
        sharedData,
        showArchived,
        showAreas,
        activeFolderIds
    );

    return {
        version: 8,
        metadata: {
            id: 'remoteShared',
        },
        sources: {source},
        layers: [
            getFillLayer({
                id: layerIds.fill,
                interaction,
                selections,
                filter,
                ...(source.type === 'vector' && {'source-layer': 'areas'}),
                isPrinting,
                visibilities,
                activeFolderIds,
            }),
            getLineLayer({
                id: layerIds.line,
                interaction,
                selections,
                filter,
                ...(source.type === 'vector' && {'source-layer': 'areas'}),
                isPrinting,
                visibilities,
                activeFolderIds,
            }),
        ],
    };
}

function getLocalSharedStyle(
    layerIds: Omit<LocalFixedLayerIds, 'point'>,
    {
        interaction,
        areas,
        sharedData,
        selections,
        isPrinting,
        showAreas,
        visibilities,
        showArchived,
        activeFolderIds,
    }: GetAreasMapboxStyleOptions
): mapboxgl.Style | undefined {
    const editingArea =
        interaction.type === 'editingArea' &&
        interaction.area &&
        isSharedData(sharedData, interaction.area) &&
        interaction.area;

    const features = compact(
        values(areas)
            .filter(
                (area: Area) =>
                    area.isGeometryLoaded &&
                    (!editingArea || area.properties.id !== editingArea.properties.id) &&
                    isSharedData(sharedData, area)
            )
            .map((area: Area): Feature<Polygon, Area['properties']> | undefined => {
                const geometry = getAreaPolygon(area, interaction);
                if (!geometry) return undefined;

                return {
                    type: 'Feature',
                    properties: area.properties,
                    geometry,
                };
            })
    );

    if (!features.length) return;

    const colorExpression: mapboxgl.Expression = [
        'get',
        ['coalesce', ['get', 'localId'], ['get', 'id']],
        ['literal', mapValues(areas, (area) => area.properties.color)],
    ];

    const filter = getLocalFilter(
        areas,
        visibilities,
        interaction,
        selections,
        sharedData,
        showArchived,
        showAreas,
        activeFolderIds
    );

    return {
        version: 8,
        metadata: {
            id: 'localShared',
        },
        sources: {
            source: {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features,
                },
            },
        },
        layers: compact([
            getFillLayer({
                id: layerIds.fill,
                interaction,
                selections,
                isPrinting,
                paint: {
                    'fill-color': colorExpression,
                },
                visibilities,
                filter,
                activeFolderIds,
            }),
            getLineLayer({
                id: layerIds.line,
                interaction,
                selections,
                isPrinting,
                paint: {
                    'line-color': colorExpression,
                },
                visibilities,
                filter,
                activeFolderIds,
            }),
        ]),
    };
}

function isSharedData(sharedData: SharedData, area: Area) {
    if (!sharedData) {
        return false;
    } else if (sharedData.type === 'area') {
        return sharedData.id === area.properties.localId;
    } else if (sharedData.type === 'folder') {
        return area.properties.folder === sharedData.id;
    } else {
        return false;
    }
}

interface GetLayerOptions extends Partial<mapboxgl.Layer> {
    interaction: InteractionState;
    id: string;
    selections: SelectionState[];
    isPrinting: boolean;
    visibilities: Record<string, boolean>;
    activeFolderIds: string[] | undefined;
}

function getFillLayer({
    interaction,
    paint,
    selections,
    isPrinting,
    visibilities,
    activeFolderIds,
    ...layer
}: GetLayerOptions): mapboxgl.FillLayer {
    return {
        type: 'fill',
        source: 'source',
        minzoom: 3,
        paint: {
            'fill-color': ['get', 'color'],
            'fill-opacity': getFocusExpression(
                interaction,
                selections,
                getVisibilityExpression(visibilities, 0.2, 0.15),
                isPrinting ? 0 : 0.05,
                0.2,
                activeFolderIds
            ),
            ...paint,
        },
        ...omit(layer, 'type'),
    };
}

function getLineLayer({
    interaction,
    paint,
    layout,
    selections,
    isPrinting,
    visibilities,
    activeFolderIds,
    ...layer
}: GetLayerOptions): mapboxgl.LineLayer {
    return {
        type: 'line',
        source: 'source',
        paint: {
            'line-color': ['get', 'color'],
            'line-width': getFocusExpression(interaction, selections, 2, 2, 2, activeFolderIds),
            'line-opacity': getFocusExpression(
                interaction,
                selections,
                getVisibilityExpression(visibilities, 1, 0.3),
                isPrinting ? 0 : 0.2,
                1,
                activeFolderIds
            ),
            ...paint,
        },
        layout: {'line-cap': 'round', ...layout},
        ...omit(layer, 'type'),
    };
}

// eslint-disable-next-line max-params
function getRemoteFilter(
    areas: Record<string, Area>,
    visibilities: Record<string, boolean>,
    interaction: InteractionState,
    selections: SelectionState[],
    sharedData: SharedData,
    showArchived: boolean,
    showAreas: boolean,
    activeFolderIds: string[] | undefined
) {
    return [
        'all',
        getLocalFilter(
            areas,
            visibilities,
            interaction,
            selections,
            sharedData,
            showArchived,
            showAreas,
            activeFolderIds
        ),
        [
            '!',
            [
                'has',
                ['get', 'id'],
                [
                    'literal',
                    fromPairs(
                        map(
                            pickBy(areas, (area) => area.isGeometryLoaded),
                            (area) => [area.properties.id, true]
                        )
                    ),
                ],
            ],
        ],
    ];
}

// eslint-disable-next-line max-params
function getLocalFilter(
    areas: Record<string, Area>,
    visibilities: Record<string, boolean>,
    interaction: InteractionState,
    selections: SelectionState[],
    sharedData: SharedData,
    showArchived: boolean,
    showAreas: boolean,
    activeFolderIds: string[] | undefined
) {
    const sharedAreaId = sharedData?.type === 'area' && sharedData.id;

    const selectedAreaIds = compact(
        [sharedAreaId].concat(
            selections.map((selection) => selection.type === 'area' && selection.localId)
        )
    );

    return [
        'all',
        [
            '!=',
            ['coalesce', ['get', 'archived'], ['get', 'deleted'], false],
            showArchived ? null : true,
        ],
        // If the areas overlay is off we should still show selected areas
        [
            'case',
            !showAreas && !(sharedData?.type === 'folder'),
            [
                'has',
                ['coalesce', ['get', 'localId'], ['get', 'id']],
                ['literal', fromPairs(selectedAreaIds.map((id) => [id, true]))],
            ],
            true,
        ],
        getFocusExpression(
            interaction,
            selections,
            getVisibilityExpression(visibilities, true, false),
            getVisibilityExpression(visibilities, true, false),
            getVisibilityExpression(visibilities, true, false),
            activeFolderIds
        ),
    ];
}

// eslint-disable-next-line max-params
function getFocusExpression<T>(
    interaction: InteractionState,
    selections: SelectionState[],
    focused: T | mapboxgl.Expression,
    blurred: T | mapboxgl.Expression,
    normal: T | mapboxgl.Expression,
    activeFolderIds: string[] | undefined
): T | mapboxgl.Expression {
    if (interaction.type === 'editingArea') {
        const localId = interaction.area.properties.localId;
        return [
            'case',
            ['==', ['coalesce', ['get', 'localId'], ['get', 'id']], localId],
            focused,
            blurred,
        ];
    } else if (interaction.type === 'editingWaypoint' || interaction.type === 'editingRoute') {
        // Always blur when a different data type is being edited
        return blurred;
    } else if (selections.length) {
        const inner = compact(
            selections.map((selection) => {
                if (selection.type === 'area') {
                    return [
                        '==',
                        ['coalesce', ['get', 'localId'], ['get', 'id']],
                        selection.localId,
                    ];
                } else {
                    return undefined;
                }
            })
        );
        if (!inner.length) return blurred;
        return ['case', ['any', ...inner], focused, blurred];
    } else if (activeFolderIds) {
        // Remote items will have a folder property, but items that have been edited locally
        // will have a parent_folder_id that is more current than the original folder from the server.
        return [
            'case',
            [
                'in',
                [
                    'case',
                    ['has', 'parent_folder_id'],
                    ['get', 'parent_folder_id'],
                    ['get', 'folder'],
                ],
                ['literal', activeFolderIds],
            ],
            focused,
            blurred,
        ];
    } else if (interaction.type !== 'normal') {
        return blurred;
    } else {
        return normal;
    }
}

function getRemoteSharedSource(sharedData: SharedData): mapboxgl.AnySourceData | undefined {
    if (!sharedData || (sharedData.type !== 'area' && sharedData.type !== 'folder')) return;

    const pubLink = sharedData.pubLink ? `?pubLink=${sharedData.pubLink}` : '';

    if (sharedData.type === 'area') {
        return {
            type: 'geojson',
            data: `${window.location.origin}/api/objects/area/${sharedData.id}/${pubLink}`,
        };
    } else if (sharedData.type === 'folder') {
        return {
            type: 'vector',
            tiles: [
                `${window.location.origin}/api/tiles/folders/${sharedData.id}/{z}/{x}/{y}.mvt${pubLink}`,
            ],
        };
    }
}

function getVisibilityExpression<T>(
    visibilities: Record<string, boolean>,
    visible: T | mapboxgl.Expression,
    hidden: T | mapboxgl.Expression
): T | mapboxgl.Expression {
    return [
        'case',
        [
            'all',
            ['has', 'id'],
            [
                'has',
                ['get', 'id'],
                ['literal', pickBy(visibilities, (visibility) => visibility === false)],
            ],
        ],
        hidden,
        visible,
    ];
}
